import type { RefObject } from 'react';
import React, { useRef, useEffect, useMemo, useContext, useLayoutEffect } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { token } from '@atlaskit/tokens';

import { BLOG_CONTENT_BLUEPRINT_ID } from '@confluence/template-utils/entry-points/templateUtils.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import type { TemplatePreviewTemplateBodiesQuery_templateBodies_PaginatedTemplateBodyList_nodes_TemplateBody as TemplateBody } from '../__types__/TemplatePreviewTemplateBodiesQuery.ts';
import {
	NoPreviewAvailable,
	CustomBlogPostPreview,
	BLOG_POST_ITEM_MODULE_COMPLETE_KEY,
} from '../PreviewMessage/index.ts';
import { PreviewAnalyticsContext } from '../PreviewAnalytics/index.ts';
import {
	isValidPreviewRepresentation,
	type TemplatePreviewTemplate,
} from '../templatePreviewHelpers.tsx';

import { AdfPreviewRenderer } from './AdfPreviewRenderer.tsx';
import { TinyPreviewRenderer } from './TinyPreviewRenderer.tsx';

type RendererProps = {
	template: TemplatePreviewTemplate;
	previewString: string;
	representation: string;
	fabricDataProviders: ProviderFactory;
};

type PreviewRendererProps = {
	templateBody?: TemplateBody;
	template: TemplatePreviewTemplate;
	isScrollable: boolean;
	error?: ApolloErrorInterop;
	marginBottom: number;
	fabricDataProviders: ProviderFactory;
};

const SCROLL_DEBOUNCE_TIME = 300;
const SCALE = 0.65;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ isScrollable: boolean; marginBottom: number }>({
	transform: `scale(${SCALE})`,
	transformOrigin: '0 0',
	width: `${100 / SCALE}%`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.isScrollable ? `${100 / SCALE}%` : 'auto'),
	padding: token('space.250'),
	overflowY: 'auto',
	boxSizing: 'border-box',
	// See <TemplateContainer> note on background paradox
	background: token('elevation.surface.overlay'),
	// this is not a hardcoded value so therefore can't be tokenized
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: `${(props) => props.marginBottom}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InteractionBlocker = styled.div({
	pointerEvents: 'none',
	userSelect: 'none',
});

const Renderer = ({
	template,
	previewString,
	representation,
	fabricDataProviders,
}: RendererProps) => {
	if (representation === 'view') {
		return <TinyPreviewRenderer previewString={previewString} />;
	}

	return (
		<AdfPreviewRenderer
			template={template}
			previewString={previewString}
			fabricDataProviders={fabricDataProviders}
		/>
	);
};

export const PreviewRenderer = ({
	templateBody,
	template,
	isScrollable,
	error,
	marginBottom,
	fabricDataProviders,
}: PreviewRendererProps) => {
	const itemModuleCompleteKey = template?.itemModuleCompleteKey;
	const contentBlueprintId = template?.contentBlueprintId;
	const previewString = templateBody?.body?.value;
	const representation = templateBody?.body?.representation;

	const routeDataRef = useRouteDataRef();
	const isLivePage = useIsCurrentPageLive();

	const { fireAnalyticsEventWithTemplateData } = useContext(PreviewAnalyticsContext);

	const wrapperRef = useRef<HTMLDivElement>();
	const prevPreviewString = useRef<string | undefined>();
	const timestamp = useRef<number>(0);

	const isPreviewAvailable =
		!!previewString && !!representation && isValidPreviewRepresentation(representation);

	if (previewString && previewString !== prevPreviewString.current) {
		timestamp.current = new Date().getTime();
	}

	useLayoutEffect(() => {
		if (previewString && previewString !== prevPreviewString.current) {
			fireAnalyticsEventWithTemplateData({
				type: 'sendTrackEvent',
				data: {
					source: 'templateDrawer',
					action: 'rendered',
					actionSubject: 'templatePreview',
					attributes: {
						renderTimeMs: new Date().getTime() - timestamp.current,
					},
				},
			});

			prevPreviewString.current = previewString;
		}
	}, [previewString, fireAnalyticsEventWithTemplateData]);

	useEffect(() => {
		if (wrapperRef.current) {
			wrapperRef.current.scrollTop = 0;
		}
	}, [previewString]);

	// Add inert attribute to Wrapper to for Voice over to skip the preview and avoid keyboard tab on some of the elements on the preview
	// TS wont recognise this attribute, so we have to manually add this to the wrapper.
	useEffect(() => {
		if (wrapperRef.current) {
			wrapperRef.current.setAttribute('inert', '');
		}
	}, []);

	useEffect(() => {
		fireAnalyticsEventWithTemplateData({
			type: 'sendScreenEvent',
			data: {
				name: 'templatePreview',
				attributes: {
					isPreviewAvailable,
					currentRouteName: routeDataRef?.current?.routeName,
					currentRouteIsLive: isLivePage,
				},
			},
		});
	}, [isPreviewAvailable, fireAnalyticsEventWithTemplateData, routeDataRef, isLivePage]);

	const handleScroll = useMemo(
		() =>
			debounce(() => {
				fireAnalyticsEventWithTemplateData({
					type: 'sendUIEvent',
					data: {
						source: 'templateDrawer',
						action: 'scrolled',
						actionSubject: 'templatePreview',
					},
				});
			}, SCROLL_DEBOUNCE_TIME),
		[fireAnalyticsEventWithTemplateData],
	);

	useEffect(() => {
		// this event is sent from useEffect to avoid sending more than one
		if (
			itemModuleCompleteKey === BLOG_POST_ITEM_MODULE_COMPLETE_KEY ||
			contentBlueprintId === BLOG_CONTENT_BLUEPRINT_ID
		) {
			fireAnalyticsEventWithTemplateData({
				type: 'sendTrackEvent',
				data: {
					source: 'templateDrawer',
					action: 'rendered',
					actionSubject: 'templatePreview',
					actionSubjectId: 'customBlogPostPreview',
				},
			});
		}
	}, [itemModuleCompleteKey, contentBlueprintId, fireAnalyticsEventWithTemplateData]);

	if (
		itemModuleCompleteKey === BLOG_POST_ITEM_MODULE_COMPLETE_KEY ||
		contentBlueprintId === BLOG_CONTENT_BLUEPRINT_ID
	) {
		return <CustomBlogPostPreview />;
	}

	if (!isPreviewAvailable) {
		fireAnalyticsEventWithTemplateData({
			type: 'sendTrackEvent',
			data: {
				source: 'templateDrawer',
				action: 'rendered',
				actionSubject: 'templatePreview',
				actionSubjectId: 'defaultImage',
			},
		});

		return <NoPreviewAvailable error={error} />;
	}

	return (
		<Wrapper
			isScrollable={isScrollable}
			marginBottom={marginBottom}
			ref={wrapperRef as RefObject<HTMLDivElement>}
			onScroll={handleScroll}
			data-testid="template-preview-scroll-wrapper"
		>
			<InteractionBlocker>
				<Renderer
					template={template}
					previewString={previewString!}
					representation={representation!}
					fabricDataProviders={fabricDataProviders}
				/>
			</InteractionBlocker>
		</Wrapper>
	);
};
